import { useState, useRef, useEffect } from 'react';
import { Spin } from 'antd';
import { useCategoryListQuery } from '../../../hooks/useCategoryListQuery';
import styles from './index.module.scss';
import { IconSearch, IconSelectArrow } from '../Icon';

interface CategorySelectorProps {
  onChange?: (data: { categoryId: string; subCategoryId: string }) => void;
}

function CategorySelector({ onChange }: CategorySelectorProps) {
  const { categories, isLoading, error } = useCategoryListQuery();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleSelect = (subId: string, subLabel: string) => {
    const category = categories.find((cat) => cat.subCategories.some((sub) => sub.id === subId));

    if (category && onChange) {
      onChange({ categoryId: category.id, subCategoryId: subId });
    }

    setSelectedId(subId);
    setSelectedLabel(subLabel);
    setSearch('');
    setIsOpen(false);
  };

  const filteredCategories = categories
    .map((cat) => ({
      ...cat,
      subCategories: cat.subCategories.filter((sub) => sub.header.toLowerCase().includes(search.toLowerCase())),
    }))
    .filter((cat) => cat.subCategories.length > 0);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current
      && !wrapperRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (isLoading) return <Spin />;
  if (error) return <div>Error loading categories</div>;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={styles.inputWrapper} onClick={() => setIsOpen(true)}>
        <input
          type="text"
          value={isOpen ? search : selectedLabel}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.input}
          placeholder="Tipping category"
        />
        <span className={styles.icon}>
          {isOpen ? <IconSearch /> : <IconSelectArrow />}
        </span>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {filteredCategories.map((cat) => (
            <div key={cat.id} className={styles.categoryGroup}>
              <div className={styles.categoryTitle}>{cat.name}</div>
              {cat.subCategories.map((sub) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                  key={sub.id}
                  className={styles.categoryOption}
                  onClick={() => handleSelect(sub.id, sub.header)}
                >
                  <input
                    type="radio"
                    name="category"
                    checked={selectedId === sub.id}
                    readOnly
                    className={styles.radio}
                  />
                  {sub.header}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CategorySelector;
